import React, { useEffect, useReducer, useState } from "react";
import clsx from "clsx";
import Carousel from "react-bootstrap/Carousel";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Currency from "../../../components/Currency";
import ImageLoader from "../../../components/ImageLoader";
import Product from "../../../components/Product";
import {
  generateItemGroup,
  getSelectedFeatures,
  AditionalFeatures
} from "../../../components/ProductDetails";

const useStyles = makeStyles(theme => ({
  backdrop: {
    width: "100vw",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.54)",
    transition: "all 0.25s",
    visibility: "hidden",
    opacity: 0,
    zIndex: 12
  },
  backdropShow: {
    visibility: "visible !important",
    opacity: "1  !important"
  },
  details: {
    width: "160vh",
    height: "80vh",
    position: "relative",
    display: "flex",
    backgroundColor: theme.palette.background.default,
    borderRadius: "5px",
    boxShadow: theme.shadows[4]
  },
  sale: {
    padding: theme.spacing(1),
    display: "inline-block",
    position: "absolute",
    top: "0",
    left: "0",
    fontSize: "1rem",
    color: theme.palette.common.white,
    background: theme.palette.secondary.main,
    borderTopLeftRadius: "5px",
    zIndex: 3
  },
  medias: {
    width: "80vh",
    height: "80vh",
    borderTopLeftRadius: "5px",
    borderBottomLeftRadius: "5px",
    borderRight: "1px solid",
    borderRightColor: theme.palette.divider,
    overflow: "hidden",
    zIndex: 2,

    "& img": {
      width: "100%",
      objectFit: "contain"
    }
  },
  right: {
    width: "80vh",
    height: "80vh",
    display: "flex",
    flexDirection: "column"
  },
  info: {
    maxHeight: "73vh",
    overflow: "auto"
  },
  item: {
    padding: theme.spacing(1, 2),
    borderBottom: "1px solid",
    borderBottomColor: theme.palette.divider,

    "&:last-child": {
      borderBottom: "none"
    }
  },
  name: {
    paddingTop: theme.spacing(2),
    fontWeight: 500,
    fontSize: "1.5rem"
  },
  description: {
    fontFamily: "inherit",
    fontSize: "0.9rem",
    whiteSpace: "pre-line",
    color: theme.palette.text.secondary
  },
  price: {
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start"
  },
  total: {
    fontWeight: 500,
    fontSize: "1.3rem",
    color: theme.palette.primary.main
  },
  full: {
    marginLeft: theme.spacing(2),
    textDecoration: "line-through",
    fontSize: "1.05rem",
    color: theme.palette.text.secondary,

    "&:before": {
      content: '"\\00a0"',
      color: "transparent"
    },
    "&:after": {
      content: '"\\00a0"',
      color: "transparent"
    }
  },
  actions: {
    height: "7vh",
    padding: theme.spacing(0, 2),
    display: "flex",
    alignItems: "center",
    borderTop: "1px solid",
    borderTopColor: theme.palette.divider,
    boxShadow: "0px -2px 8px 2px rgba(0, 0, 0, 0.2)"
  },
  label: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  },
  quantity: {
    width: "4rem",
    padding: theme.spacing(1),
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: "5px"
  },
  featureName: {
    fontWeight: 500,
    fontSize: "1.15rem"
  },
  featureOptions: {
    width: "100%"
  },
  featureGroup: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start"
  },
  featureOption: {
    width: "25%",
    marginRight: theme.spacing(2),
    overflow: "auto"
  },
  grow: {
    flexGrow: 1
  },
  add: {
    borderRadius: "30px"
  },
  close: {
    width: "35px",
    height: "35px",
    position: "absolute",
    top: "-17px",
    right: "-17px",
    fontSize: "1.15rem",
    color: theme.palette.common.white,
    background: "#424242",
    border: "none",
    borderRadius: "30px",
    boxShadow: theme.shadows[2]
  },
  noStock: {
    height: "25px",

    "&:after": {
      content: '"Agotado"',
      padding: theme.spacing(1),
      fontSize: "1rem",
      color: theme.palette.primary.main,
      border: "1px solid",
      borderColor: theme.palette.primary.main,
      borderRadius: "3px"
    }
  },
  maxStock: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main
  }
}));

const initialState = {
  item: null,
  index: null,
  quantity: 0,
  total: 0,
  features: []
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "quantity":
    case "features":
      return { ...state, [type]: payload };
    case "item":
      return {
        ...state,
        ...payload
      };
    case "update":
      return {
        ...state,
        item: payload.item,
        quantity: payload.quantity
      };
    default:
      return { ...state };
  }
};

const ProductDetails = ({ items, product, onChange, onClose }) => {
  const classes = useStyles();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [stock, setStock] = useState(null);

  useEffect(() => {
    let features = [];

    if (product) {
      if (product.body && product.body.features) {
        features = JSON.parse(JSON.stringify(product.body.features));

        const stock = Product.getStock(product, features);
        setStock(stock);
      }
    }

    dispatch({
      type: "features",
      payload: features
    });
  }, [product]);

  useEffect(() => {
    if (product) {
      let item = null;
      let index = null;
      let quantity = 1;
      let total =
        product.body.sale && product.body.sale.percentage ? product.body.sale.value : product.price;

      const group = generateItemGroup(product.id, state.features);

      for (let i = 0; i < items.length; i++) {
        if (items[i].group === group) {
          item = items[i];
          index = i;
          quantity = items[i].quantity;
          total = quantity * item.price;
          break;
        }
      }

      if (!item) {
        item = {
          productID: product.id,
          price:
            product.body.sale && product.body.sale.percentage
              ? product.body.sale.value
              : product.price,
          quantity,
          total: product.price * quantity,
          body: { features: state.features },
          group,
          product
        };
      }

      dispatch({
        type: "item",
        payload: {
          item,
          index,
          quantity,
          total
        }
      });
    }
  }, [items, product, state.features]);

  const updateItem = quantity => {
    const selectedFeatures = getSelectedFeatures(state.features);

    const item = {
      ...state.item,
      quantity,
      total: state.item.price * quantity,
      body: { features: selectedFeatures },
      product
    };

    const group = generateItemGroup(product.id, selectedFeatures);

    if (group) {
      item.group = group;
    }

    const action = state.index === null ? "create" : quantity > 0 ? "update" : "delete";

    dispatch({
      type: "item",
      payload: {
        item,
        quantity
      }
    });

    onChange(action, item, state.index);
  };

  const canAdd = () => {
    if (state.features.length > 0) {
      for (let i = 0; i < state.features.length; i++) {
        if (!Boolean(state.features[i].option)) {
          return false;
        }
      }
    }

    return true;
  };

  const addButton = () => {
    if (stock && stock.quantity === 0) {
      return <div className={classes.noStock}></div>;
    }

    return (
      <Button
        className={classes.add}
        variant="contained"
        color="secondary"
        disabled={!canAdd()}
        onClick={add}
      >
        Agregar <Currency value={state.total} />
      </Button>
    );
  };

  const setQuantity = ({ target }) => {
    if (
      target.value >= 1 &&
      target.value <= 999 &&
      (stock && target.value > stock.quantity ? false : true)
    ) {
      dispatch({
        type: "item",
        payload: {
          quantity: target.value,
          total: target.value * state.item.price
        }
      });
    }
  };

  const selectFeature = (index, option) => {
    if (index >= 0) {
      const features = [...state.features];
      features[index].option = option;

      const stock = Product.getStock(product, features);
      setStock(stock);

      dispatch({
        type: "features",
        payload: features
      });
    }
  };

  const add = () => {
    updateItem(state.quantity);
    onClose();
  };

  return (
    <div className={clsx(classes.backdrop, product ? classes.backdropShow : {})}>
      {product && (
        <div className={classes.details}>
          {product.body.sale && product.body.sale.percentage && (
            <div className={classes.sale}>{product.body.sale.percentage}%</div>
          )}
          <div className={classes.medias}>
            <Carousel interval={4000}>
              {product.medias &&
                product.medias.map((file, i) => (
                  <Carousel.Item key={`media-${i}`}>
                    <ImageLoader imgKey={file && file.key} />
                  </Carousel.Item>
                ))}
            </Carousel>
          </div>

          <div className={classes.right}>
            <div className={classes.info}>
              <div className={clsx(classes.item, classes.name)}>{product.name}</div>
              {product.body.description && (
                <pre className={clsx(classes.item, classes.description)}>
                  {product.body.description}
                </pre>
              )}

              <div className={clsx(classes.item, classes.price)}>
                {product.body.sale && product.body.sale.percentage ? (
                  <>
                    <div className={classes.total}>
                      <Currency value={product.body.sale.value} />
                    </div>
                    <div className={classes.full}>
                      <Currency value={product.price} />
                    </div>
                  </>
                ) : (
                  <div className={classes.total}>
                    <Currency value={product.price} />
                  </div>
                )}
              </div>

              {product.body.aditionalFeatures && product.body.aditionalFeatures.status && (
                <AditionalFeatures product={product} />
              )}

              {state.features.map((feature, i) => (
                <div key={`feature-${i}`} className={classes.item}>
                  <div className={classes.featureName}>{feature.label}</div>
                  <FormControl variant="outlined" className={classes.featureOptions} size="small">
                    <RadioGroup
                      className={classes.featureGroup}
                      aria-label={feature.label}
                      id={`feature-select-${i}`}
                      value={feature.option || ""}
                    >
                      {feature.options.map((option, j) => (
                        <FormControlLabel
                          className={classes.featureOption}
                          key={`option-${i}-${j}`}
                          value={option}
                          control={<Radio />}
                          label={option.label}
                          onChange={() => selectFeature(i, option)}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </div>
              ))}
            </div>

            <div className={classes.grow}></div>

            <div className={classes.actions}>
              <span className={classes.label}>Cantidad</span>
              <input
                className={classes.quantity}
                type="number"
                size={3}
                min={1}
                max={999}
                maxLength={3}
                value={state.quantity}
                onChange={setQuantity}
              />

              {stock && state.quantity >= stock.quantity && (
                <span className={classes.maxStock}>Máximo</span>
              )}

              <div className={classes.grow}></div>

              {addButton()}
            </div>
          </div>

          <button className={classes.close} onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
